import { Inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { environment } from '@env/environment';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { v4 as uuidv4 } from 'uuid';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/** sg_beacon should be loaded as a script on the index.html head, else, this service will throw an error */
declare function sg_beacon(action: string, token: string);

@Injectable()
export class AlchemerSurveyService {
  private surveyToken = environment.surveyToken;
  private routesToAvoid = ['/pdf/'];
  private sessionUUID: string;
  private globalWindow = window as any;

  constructor(
    private location: Location,
    private router: Router,
    private userInfoResolver: UserInfoResolver,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.sessionUUID = uuidv4(); // Generate a UUID for this session
    this.onRouteChange();
  }

  init() {
    if (sg_beacon == null) {
      throw new Error('Alchemer beacon not loaded correctly');
    }
    setTimeout(() => {
      sg_beacon('init', this.surveyToken);
      this.setUserData();
    }, 0);
  }

  /**
   * Sets user data for the survey beacon.
   */
  private setUserData(): void {
    const user = this.userInfoResolver.resolve();
    const userId = user?.id?.toString() || 'anonymous';
    const uuid = this.sessionUUID;
    const language = user?.userLocaleId?.toString() || '1';
    let userType = 'unknown';

    if (user) {
      if (this.userInfoResolver.isUsBroker()) {
        userType = 'Broker';
      } else if (this.userInfoResolver.isUsResearcher()) {
        userType = 'Researcher';
      } else {
        userType = user.userTypeVal || 'unknown';
      }
    }

    this.globalWindow.sg_beacon('data', 'UserID', userId);
    this.globalWindow.sg_beacon('data', 'UUID', uuid);
    this.globalWindow.sg_beacon('data', 'Language', language);
    this.globalWindow.sg_beacon('data', 'UserType', userType);
  }

  private onRouteChange() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const pathContainsInvalidRoute = this.routesToAvoid.some(routeToAvoid =>
        this.location.path().includes(routeToAvoid)
      );
      if (pathContainsInvalidRoute) {
        return;
      }

      try {
        this.init();
      } catch (e) {
        this.logger.warn(e);
      }
    });
  }
}
